import type { IRiskCatalogueScenarioCode } from '@/lib/constants/riskCatalogueScenarios'

// NAME becomes the risk name (risk_name is varchar 255) and DESCRIPTION its notes, in the creator's language.
export const RISK_CATALOGUE_SCENARIO_TEXTS = {
  'RS-001': {
    NAME: 'Operations halt without a tested continuity plan',
    DESCRIPTION: 'Critical operations halt due to lack of continuity planning (no tested BCP in place).',
  },
  'RS-002': {
    NAME: 'Prolonged outage of a critical SaaS vendor',
    DESCRIPTION:
      'A critical SaaS vendor suffers a prolonged outage. No adequate SLA, no vendor resilience assessment, and no failover provider exist.',
  },
  'RS-003': {
    NAME: 'Regional power outage in the cloud availability zone',
    DESCRIPTION:
      'A regional power outage affects the cloud availability zone. Architecture relies on a single AZ with no failover and no tested DR plan.',
  },
  'RS-004': {
    NAME: 'Internet provider backbone outage',
    DESCRIPTION:
      'The primary ISP suffers a backbone outage. Cloud services become unreachable due to single point of failure and no redundant routing.',
  },
  'RS-005': {
    NAME: 'Network equipment failure without redundancy',
    DESCRIPTION:
      'Network equipment fails and no redundant connectivity exists. Remote staff lose access to cloud services and cannot maintain operations.',
  },
  'RS-006': {
    NAME: 'Cloud service failure from an unpatched software flaw',
    DESCRIPTION:
      'A core cloud service fails due to a known but unpatched software flaw. No vulnerability mitigation process prioritizes remediation.',
  },
  'RS-007': {
    NAME: 'DDoS attack on cloud infrastructure',
    DESCRIPTION:
      'A DDoS attack overwhelms cloud infrastructure. No VPC segmentation contains the blast radius and auto-scaling is misconfigured.',
  },
  'RS-008': {
    NAME: 'Configuration drift causing a cascading failure',
    DESCRIPTION:
      'Infrastructure drifts from intended configuration over time. No IaC scanning detects drift; a routine update causes cascading failure.',
  },
  'RS-009': {
    NAME: 'Targeted cyber sabotage attack',
    DESCRIPTION:
      'A targeted cyber-sabotage attack exploits insecure network architecture. No incident response plan exists and breach monitoring is inadequate.',
  },
  'RS-010': {
    NAME: 'Spear phishing against new staff',
    DESCRIPTION: 'A spear-phishing campaign targets new staff who joined without a formal security onboarding process.',
  },
  'RS-011': {
    NAME: 'Interception of unencrypted API traffic',
    DESCRIPTION: 'An attacker intercepts unencrypted API traffic over unprotected communication lines.',
  },
  'RS-012': {
    NAME: 'Eavesdropping on unencrypted communications',
    DESCRIPTION: 'Unencrypted communications are intercepted, including credentials transmitted in clear text.',
  },
  'RS-013': {
    NAME: 'Exfiltration of sensitive documents from cloud storage',
    DESCRIPTION:
      'Sensitive documents are exfiltrated from cloud storage. No data classification policy exists; sensitive files are unprotected and unmarked.',
  },
  'RS-014': {
    NAME: 'Cloud console credential theft without MFA',
    DESCRIPTION:
      'Cloud console credentials are stolen due to missing MFA. The attacker gains full administrative access to the cloud environment.',
  },
  'RS-015': {
    NAME: 'Exposed database backup with customer personal data',
    DESCRIPTION:
      'A production database backup with customer PII is accidentally exposed via misconfigured cloud storage permissions.',
  },
  'RS-016': {
    NAME: 'Injection attack on an API endpoint',
    DESCRIPTION:
      'An API endpoint lacking input validation is exploited via injection attacks. Insufficient testing and no IaC scanning miss the gap.',
  },
  'RS-017': {
    NAME: 'CI/CD pipeline compromise through hardcoded secrets',
    DESCRIPTION:
      'The CI/CD pipeline is compromised due to hardcoded secrets in source code. Malicious code is injected into production builds.',
  },
  'RS-018': {
    NAME: "Browser exploit on a remote worker's device",
    DESCRIPTION:
      'A remote worker visits a compromised website exploiting a browser vulnerability. Weak endpoint protection and no security onboarding leave the risk unmanaged.',
  },
  'RS-019': {
    NAME: 'Man-in-the-middle attack on API communications',
    DESCRIPTION:
      'An attacker performs a MITM (man-in-the-middle) attack on API communications, replaying stolen tokens due to weak authentication and missing MFA.',
  },
  'RS-020': {
    NAME: 'Personal data processed without a DPIA',
    DESCRIPTION: 'Personal data is processed for a new feature without conducting a DPIA.',
  },
  'RS-021': {
    NAME: 'Production database exposed by a misconfigured security group',
    DESCRIPTION:
      'A cloud security group is misconfigured, exposing a production database to the internet. No CSPM detects it.',
  },
  'RS-022': {
    NAME: 'Exploited vulnerability in unapproved software',
    DESCRIPTION:
      'Unapproved open-source libraries and unlicensed SaaS are adopted without vendor assessment. One contains a vulnerability that is exploited.',
  },
  'RS-023': {
    NAME: 'Production database corruption with untested backups',
    DESCRIPTION:
      'A faulty deployment corrupts the primary cloud database. Backups are untested, cloud logging is insufficient, and corruption propagates before detection.',
  },
  'RS-024': {
    NAME: 'Personal data shared without a legal basis',
    DESCRIPTION:
      'Customer personal data is shared with a third party without legal basis or data processing agreements.',
  },
  'RS-025': {
    NAME: 'Malware spread from unvetted software',
    DESCRIPTION:
      'Malware infects a corporate device via unvetted software. No vulnerability mitigation process or endpoint protection prevents its spread to cloud storage.',
  },
  'RS-026': {
    NAME: 'Accidental deletion or permission change in the cloud console',
    DESCRIPTION:
      'A critical error in the cloud console accidentally deletes a production resource or alters IAM permissions. No CSPM guardrails prevent dangerous actions.',
  },
  'RS-027': {
    NAME: 'Insider abuse of overly permissive IAM roles',
    DESCRIPTION:
      'An insider with overly permissive IAM roles accesses customer databases beyond their role. No periodic access review or role recertification process exists.',
  },
  'RS-028': {
    NAME: 'Forged IAM or OAuth tokens',
    DESCRIPTION: 'An attacker forges IAM or OAuth tokens exploiting weak authentication and missing MFA.',
  },
  'RS-029': {
    NAME: 'Untraceable configuration change',
    DESCRIPTION:
      'A configuration change that caused a production incident cannot be attributed. Cloud logging is missing and audit trail configuration is insufficient.',
  },
  'RS-030': {
    NAME: 'Operations degraded by concentrated security expertise',
    DESCRIPTION:
      'No dedicated security role, poor knowledge transfer, and concentrated expertise leave operations degraded.',
  },
  'RS-031': {
    NAME: 'Underfunded security program',
    DESCRIPTION:
      'Security investment is deprioritized due to limited budget. No CISO role exists and risk assessments are not performed.',
  },
  'RS-032': {
    NAME: 'Sudden shutdown of a key cloud or SaaS vendor',
    DESCRIPTION:
      'A key cloud or SaaS vendor ceases operations abruptly. No vendor assessment or contingency plan exists for provider failure.',
  },
  'RS-033': {
    NAME: 'Failed GDPR compliance audit',
    DESCRIPTION:
      'The organization fails a GDPR/LOPDGDD compliance audit. No DPO is appointed, no regular audits, and RPA is incomplete.',
  },
  'RS-034': {
    NAME: 'Fire in the server room or telecom rack area',
    DESCRIPTION:
      'A fire breaks out in the area housing the telecom rack or server room. Insufficient physical protection and no BCP for physical asset loss.',
  },
  'RS-035': {
    NAME: 'Water damage in the telecom rack area',
    DESCRIPTION:
      'Water damage from a burst pipe or leak affects the telecom rack area. Insufficient protection and an outdated BCP leave no quick recovery path.',
  },
  'RS-036': {
    NAME: 'Theft of laptops or devices',
    DESCRIPTION: 'Laptops or devices are stolen due to inadequate physical access controls.',
  },
  'RS-037': {
    NAME: 'Unauthorized access to the server room',
    DESCRIPTION:
      'An unauthorized person accesses the server room or rack area. Inadequate physical controls and unsupervised external staff allow potential device tampering.',
  },
  'RS-038': {
    NAME: 'Administrative account takeover',
    DESCRIPTION: 'No MFA, shared admin passwords, and no login anomaly alerts enable full administrative takeover.',
  },
  'RS-039': {
    NAME: 'Publicly accessible cloud storage bucket',
    DESCRIPTION:
      'A cloud storage bucket is left publicly accessible. No CSPM detects the exposure; automated scanners discover and index customer data within hours.',
  },
  'RS-040': {
    NAME: 'Known critical vulnerability in a production container image',
    DESCRIPTION:
      'A container image in production contains a known critical vulnerability. No formal vulnerability management process exists to triage and remediate it.',
  },
  'RS-041': {
    NAME: 'Compromised third-party CI/CD tool or package',
    DESCRIPTION:
      'A compromised third-party CI/CD tool or package injects malicious code. Weak vendor contracts, no vendor assessment, and uncontrolled adoption enable it.',
  },
  'RS-042': {
    NAME: 'Customer data downloaded through broken API authentication',
    DESCRIPTION:
      'An insecure API endpoint with broken authentication is exploited to enumerate and download customer records.',
  },
  'RS-043': {
    NAME: 'Cross-tenant data access in a multi-tenant product',
    DESCRIPTION:
      'A bug in a multi-tenant SaaS product allows cross-tenant data access due to missing VPC segmentation and insecure architecture.',
  },
  'RS-044': {
    NAME: 'Code injection through a serverless function',
    DESCRIPTION: 'Malicious code is injected through a serverless function event source with no input validation.',
  },
  'RS-045': {
    NAME: 'Backdoor injected through a compromised CI/CD pipeline',
    DESCRIPTION:
      'The CI/CD pipeline is compromised via a vulnerable dependency or stolen developer credentials. Hardcoded secrets and weak change control enable a backdoor injection.',
  },
  'RS-046': {
    NAME: 'Unapproved SaaS tools adopted by employees',
    DESCRIPTION:
      'Employees adopt unapproved SaaS tools without IT oversight. No onboarding process communicates approved tools or acceptable use policies.',
  },
  'RS-047': {
    NAME: 'Misconfigurations multiplying without cloud governance',
    DESCRIPTION: 'No CSPM, no change control, no risk assessment. Misconfigurations multiply.',
  },
  'RS-048': {
    NAME: 'Unresolved incident due to missing backup personnel',
    DESCRIPTION:
      'No backup personnel, no documented credentials, and no knowledge transfer. A production incident goes unresolved.',
  },
  'RS-049': {
    NAME: 'Session theft on public Wi-Fi',
    DESCRIPTION:
      'A remote worker uses public Wi-Fi without VPN. Weak endpoint protection allows session cookies and tokens to be captured, granting access to cloud services.',
  },
  'RS-050': {
    NAME: 'Former employee keeps access',
    DESCRIPTION:
      'A departing employee retains access to cloud services and repositories due to lack of a formal offboarding process.',
  },
  'RS-052': {
    NAME: 'Large-scale personal data processing without a DPO',
    DESCRIPTION:
      'The organization processes EU personal data at scale without appointing a DPO, maintaining RPA, or conducting audits.',
  },
  'RS-053': {
    NAME: 'Late breach notification to the AEPD',
    DESCRIPTION:
      'After a data breach, the organization fails to notify the AEPD within 72 hours. No breach notification procedure or adequate monitoring exists.',
  },
  'RS-054': {
    NAME: 'International data transfers without safeguards',
    DESCRIPTION:
      'Customer data is transferred outside the EEA without SCCs (Standard Contractual Clauses) or other safeguards. No DPA with sub-processors and insufficient contract provisions exist.',
  },
  'RS-057': {
    NAME: 'Tracking cookies without consent',
    DESCRIPTION:
      'Tracking cookies and analytics run without proper consent management per LSSI-CE. No compliant cookie banner is implemented.',
  },
  'RS-058': {
    NAME: 'Improvised response to a security incident',
    DESCRIPTION:
      'A security incident occurs but the organization has no formal incident management process. Detection, containment, and communication are improvised and delayed.',
  },
  'RS-059': {
    NAME: 'Accidental data exposure by new employees',
    DESCRIPTION:
      'New employees receive access to production systems without a formal security onboarding. Misconfigured permissions and poor awareness lead to accidental data exposure.',
  },
  'RS-062': {
    NAME: 'Traffic spike overwhelming cloud infrastructure',
    DESCRIPTION:
      'An unexpected traffic spike overwhelms cloud infrastructure. Auto-scaling is misconfigured and no capacity planning or load testing has been performed.',
  },
  'RS-063': {
    NAME: 'Hardcoded API keys and secrets in repositories',
    DESCRIPTION:
      'API keys and secrets are hardcoded in source code repositories. An attacker or former employee discovers them and gains unauthorized access.',
  },
  'RS-064': {
    NAME: 'Lost or stolen laptop without encryption or remote wipe',
    DESCRIPTION:
      "An employee's laptop is lost or stolen while traveling. Without MDM/remote-wipe or full-disk encryption, sensitive corporate and customer data is exposed and cannot be remotely revoked.",
  },
  'RS-065': {
    NAME: 'Critical hardware failure due to age or wear',
    DESCRIPTION:
      'A critical laptop, NAS/backup device, or on-prem server fails unexpectedly due to age or wear. Local backups become temporarily unavailable.',
  },
  'RS-066': {
    NAME: 'Rogue USB device or hardware keylogger',
    DESCRIPTION:
      'A rogue USB device or hardware keylogger is connected to an employee workstation during an unsupervised visit, capturing credentials undetected due to a lack of endpoint/USB port controls.',
  },
  'RS-067': {
    NAME: 'Prompt injection in an LLM-based feature',
    DESCRIPTION:
      'An attacker injects malicious instructions via user input (prompt injection) into a production LLM-based feature, or manipulates a third-party foundation model into leaking system prompts or bypassing safety guardrails, due to insufficient input validation and no adversarial testing before deployment.',
  },
  'RS-068': {
    NAME: 'Biased or hallucinated outputs from an in-house AI model',
    DESCRIPTION:
      'An in-house AI model trained on poorly governed or unrepresentative data produces biased or hallucinated outputs in production. The issue goes undetected due to lack of ongoing model monitoring/drift detection, leading to incorrect business decisions or customer harm.',
  },
} satisfies Record<IRiskCatalogueScenarioCode, { NAME: string; DESCRIPTION: string }>

export const RISK_CATALOGUE = {
  NEW_RISK_OPTIONS: 'More ways to create a risk',
  FROM_CATALOGUE: 'From the catalogue',
  BLANK_RISK: 'Blank',
  TITLE: 'Risk Catalogue',
  DESCRIPTION: 'Add ISO 27005 scenarios as risks, with threats and vulnerabilities already filled in.',
  SEARCH: 'Search scenario, threat or vulnerability',
  ALL_CATEGORIES: 'All categories',
  COLUMNS: {
    CATEGORY: 'Asset category',
    ID: 'ID',
    SCENARIO: 'Scenario',
    THREAT: 'Threat',
    VULNERABILITIES: 'Vulnerabilities',
    IMPACT: 'Impact',
  },
  NO_RESULTS: 'No scenarios found',
  NONE_SELECTED: 'No scenario selected',
  SELECTED_COUNT: (count: number) => `${count} ${count === 1 ? 'scenario' : 'scenarios'} selected`,
  CANCEL: 'Cancel',
  ADD: (count: number) => (count > 1 ? `Add ${count} risks` : 'Add risk'),
  ADDED: (count: number) => `${count} ${count === 1 ? 'risk' : 'risks'} added`,
}
